import React, { Suspense, useCallback, useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import AppSidebar from "../app-sidebar/AppSidebar";
import BoardTour from "@features/tour/BoardTour";
import { useTour } from "@contexts/TourContext";
import { useMobileSidebar } from "@contexts/MobileSidebarContext";
import LoadingScreen from "@components/LoadingScreen/LoadingScreen";
import {
  getSidebarCollapsed,
  setSidebarCollapsed,
} from "@utils/sidebarPreferences";
import "./BoardWithSidebarLayout.scss";

const BoardWithSidebarLayout: React.FC = () => {
  // Read once on mount rather than on every render — the stored value only
  // changes through the toggle below, which owns the state from then on.
  const [isSidebarCollapsed, setIsSidebarCollapsed] =
    useState<boolean>(getSidebarCollapsed);
  const {
    isOpen: isMobileSidebarOpen,
    close: handleMobileClose,
    registerSidebar,
  } = useMobileSidebar();
  const { startTour } = useTour();

  // Lets the header's mobile menu button know there is a drawer to open.
  useEffect(() => registerSidebar(), [registerSidebar]);

  useEffect(() => {
    // One frame delay ensures board route children (and data-tour elements) are mounted
    const id = setTimeout(() => startTour(), 0);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCollapse = useCallback(() => {
    const next = !isSidebarCollapsed;
    setIsSidebarCollapsed(next);
    setSidebarCollapsed(next);
  }, [isSidebarCollapsed]);

  return (
    <div className="board-with-sidebar">
      <AppSidebar
        isCollapsed={isSidebarCollapsed}
        isMobileOpen={isMobileSidebarOpen}
        onCollapse={handleCollapse}
        onMobileClose={handleMobileClose}
      />
      <div className="board-with-sidebar__content">
        <Suspense fallback={<LoadingScreen inline />}>
          <Outlet />
        </Suspense>
      </div>
      <BoardTour />
    </div>
  );
};

export default BoardWithSidebarLayout;
